import { Col, Flex, Space, Typography } from "antd";
import { PermissionIntegrationLogo } from "components/Integrations/IntegrationLogo";
import { DOCS_URLS } from "constants/docsUrls";
import React, { ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { spacing } from "styles/spacing";

import { Splash } from "../Splash";
import { SkipOnboardingButton } from "./components/SkipOnboardingButton";
import { CloudProviderContainer } from "./steps/CloudProviderContainer";
import { SupportedCloudProviders } from "./types";

const providerRequiredPrivileges: (
  flow?: string,
  cloudProvider?: SupportedCloudProviders
) => ReactElement | undefined = (flow, cloudProvider) => {
  const privilegeText = `You'll need to have elevated privileges in your ${
    cloudProvider === "aws"
      ? "AWS account"
      : cloudProvider === "azure"
        ? "Azure organization"
        : "Google Cloud project"
  } in order to install P0.`;
  switch (cloudProvider) {
    case "aws":
      return (
        <Typography.Paragraph>
          {privilegeText} We recommend using the{" "}
          <Link
            to="https://docs.aws.amazon.com/aws-managed-policy/latest/reference/IAMFullAccess.html"
            target="_blank"
            rel="noreferrer"
          >
            IAMFullAccess
          </Link>{" "}
          managed policy.
        </Typography.Paragraph>
      );
    case "azure":
      return (
        <Typography.Paragraph>
          {privilegeText} We recommend using the{" "}
          <Typography.Text code>Owner</Typography.Text> role for ease of use.
        </Typography.Paragraph>
      );
    case "gcloud":
      return (
        <Space
          direction="vertical"
          size={spacing.layout.grid.gap.xs}
          style={{ marginBottom: spacing.scale.lg }}
        >
          <Typography.Paragraph>
            {privilegeText} We recommend using the{" "}
            <Typography.Text code>Editor</Typography.Text> role for ease of use.
          </Typography.Paragraph>
          {flow === "jit" && (
            <>
              <Typography.Text>
                You can use more fine-grained permissions if you prefer. Your
                user will need permissions to:
              </Typography.Text>
              <Typography.Text>
                - Create and manage IAM service accounts
              </Typography.Text>
              <Typography.Text>
                - Enable Google Cloud services in your project
              </Typography.Text>
              <Typography.Text>
                - Create and manage custom roles
              </Typography.Text>
              <Typography.Text>
                - Create and deploy a new Cloud Run service
              </Typography.Text>
              <Typography.Text>
                - Add role bindings to your Google Cloud project
              </Typography.Text>
            </>
          )}
          {flow === "assessment" && (
            <>
              <Typography.Text>
                You can use more fine-grained permissions if you prefer. Your
                user will need permissions to:
              </Typography.Text>
              <Typography.Text>
                - Create and manage custom roles
              </Typography.Text>
              <Typography.Text>
                - Add role bindings to your Google Cloud project
              </Typography.Text>
            </>
          )}
        </Space>
      );
    default:
      return undefined;
  }
};

const providerDocLinks: Record<SupportedCloudProviders, string> = {
  aws: DOCS_URLS.AWS_INSTALL,
  gcloud: DOCS_URLS.GCLOUD_INSTALL,
  azure: DOCS_URLS.AZURE_INSTALL,
};

const ProviderContent: React.FC<{
  cloudProvider: SupportedCloudProviders;
}> = ({ cloudProvider }) => {
  const [searchParams] = useSearchParams();
  const flow = searchParams.get("flow") || undefined;

  return (
    <>
      <Typography.Paragraph>
        In order to set up P0 Security in your cloud, you will need to install
        some items in your environment.
      </Typography.Paragraph>
      {providerRequiredPrivileges(flow, cloudProvider)}
      <Typography.Paragraph>
        You can find out more in our&nbsp;
        <Link
          to={providerDocLinks[cloudProvider]}
          target="_blank"
          rel="noreferrer"
        >
          official documentation
        </Link>
        .
      </Typography.Paragraph>
    </>
  );
};

const OnboardWorkflows = React.memo(() => {
  const { cloudProvider } = useParams<{
    cloudProvider: SupportedCloudProviders;
    component: string;
  }>();

  return (
    <Splash overrideColumn rowStyleOverride={{ alignItems: "center" }}>
      <Col xs={22} md={11} lg={8} xl={6}>
        <Flex align="center" gap={spacing.layout.grid.gap.xs}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Install P0 for
          </Typography.Title>
          {cloudProvider && <>{PermissionIntegrationLogo[cloudProvider]}</>}
        </Flex>
        {cloudProvider && <ProviderContent cloudProvider={cloudProvider} />}
        <SkipOnboardingButton />
      </Col>
      <Col xs={22} md={12} lg={12} xl={10}>
        <CloudProviderContainer />
      </Col>
    </Splash>
  );
});

OnboardWorkflows.displayName = "OnboardWorkflows";

export { OnboardWorkflows };
